import React from "react";
import { Dialog, Button, Typography, IconButton } from "@material-tailwind/react";
import { t } from "i18next";
import { WorkorderStatus } from "../../../types/workorder/workorderStatus";
import Switch from "../../switch/Switch";
import Loading from "../../loading/Loading";
import CustomerDisplay from "../customer/CustomerDisplay";
import CustomerEditButton from "../../export-button/CustomerEditButton";
import { EyeIcon, ExclamationTriangleIcon, DocumentChartBarIcon } from "@heroicons/react/24/solid";
import { XMarkIcon } from "@heroicons/react/24/outline";

interface StatusChangeConfirmDialogProps {
  open: boolean;
  status: WorkorderStatus | null;
  statusBgClass: string;
  statusIcon: React.ElementType | null;
  workorderRef: string;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  workorder?: EasyTech.Workorder;
  /** Detailed customer (GET /users/customers/{id}); workorder.customer is the simple response. */
  customerDetail?: EasyTech.Customer | null;
  customerLanguages?: any[];
  isLoadingLanguages?: boolean;
  sendReportEmail?: boolean;
  setSendReportEmail?: (value: boolean) => void;
  handleViewPdf?: (languageIsoCode: string) => void;
  handleDownloadPdf?: (languageIsoCode: string) => void;
  handleEditCustomer?: () => void;
  onCustomerUpdate?: (updatedCustomer: EasyTech.Customer) => Promise<void>;
}

/**
 * StatusChangeConfirmDialog: Generic dialog for workorder status change confirmation
 * @param props - Component props
 */
export default function StatusChangeConfirmDialog({
  open,
  status,
  statusBgClass,
  statusIcon: StatusIcon,
  workorderRef,
  onClose,
  onConfirm,
  loading = false,
  workorder,
  customerDetail = null,
  customerLanguages = [],
  isLoadingLanguages = false,
  sendReportEmail = false,
  setSendReportEmail,
  handleViewPdf,
  handleDownloadPdf,
  handleEditCustomer,
  onCustomerUpdate,
}: StatusChangeConfirmDialogProps) {
  /**
   * Get the description message based on the status
   * @param status - The workorder status
   * @returns The description message
   */
  const getStatusDescription = (status: WorkorderStatus | null): string => {
    if (!status) return "";

    switch (status) {
    case WorkorderStatus.IN_TRANSIT:
      return t("workorder:status:text_in_transit");
    case WorkorderStatus.IN_PROGRESS:
      return t("workorder:status:text_in_progress");
    case WorkorderStatus.SUSPENDED:
      return t("workorder:status:text_suspend");
    case WorkorderStatus.CANCELED:
      return t("workorder:status:text_cancel");
    case WorkorderStatus.CREATED:
      return t("workorder:status:text_resume");
    case WorkorderStatus.VALIDATED:
      return t("workorder:status:text_validate");
    case WorkorderStatus.INVOICED:
      return t("workorder:status:text_invoiced");
    case WorkorderStatus.PAID:
      return t("workorder:status:text_paid");
    case WorkorderStatus.CLOSED:
      return t("workorder:status:text_close");
    case WorkorderStatus.ACCEPTED:
      return t("workorder:status:text_accepted");
    }
  };

  const isValidation = status === WorkorderStatus.VALIDATED;
  
  const customerEmails = React.useMemo(() => {
    if (!workorder?.customer) return [];
    const emails: string[] = [];
    
    if (workorder.customer.contactInfo?.email) {
      emails.push(workorder.customer.contactInfo.email);
    }
    
    if (workorder.customer.contactInfo?.emails && Array.isArray(workorder.customer.contactInfo.emails)) {
      workorder.customer.contactInfo.emails.forEach(email => {
        if (email && !emails.includes(email)) {
          emails.push(email);
        }
      });
    }
    
    return emails;
  }, [workorder]);

  const isEmailMissing = customerEmails.length === 0;
  const isLanguageMissing = !customerLanguages || customerLanguages.length === 0;
  // While the customer is still loading its report-email preference is unknown, so the toggle would
  // be showing a default the customer has not confirmed. Hide the section and block confirmation
  // rather than let the user act on it.
  const isCustomerLoading = isValidation && isLoadingLanguages;
  const isConfirmDisabled = loading
    || isCustomerLoading
    || (isValidation && sendReportEmail && (isEmailMissing || isLanguageMissing));

  return (
    <Dialog open={open} handler={onClose} size={isValidation ? "sm" : "xs"} className="outline-none">
      <div className="p-4 relative">
        {isValidation && (
          <IconButton
            variant="text"
            color="blue-gray"
            onClick={onClose}
            className="!absolute top-2 right-2"
          >
            <XMarkIcon className="h-5 w-5" />
          </IconButton>
        )}

        <div className="flex items-center gap-3 mb-2">
          {StatusIcon && (
            <div className={`p-2 rounded-full ${statusBgClass}`}>
              <StatusIcon className="h-5 w-5 text-white" />
            </div>
          )}
          <Typography variant="h5">
            {status && (
              workorderRef 
                ? `${t([`workorder:action:${status.toLowerCase()}`, `workorder:status:${status.toLowerCase()}`])} #${workorderRef}`
                : t([`workorder:action:${status.toLowerCase()}`, `workorder:status:${status.toLowerCase()}`])
            )}
          </Typography>
        </div>
        
        <Typography className="mb-4">
          {getStatusDescription(status)}
        </Typography>

        {isCustomerLoading && (
          <div className="flex items-center justify-center p-4">
            <Loading />
          </div>
        )}

        {isValidation && workorder && !isCustomerLoading && (
          <div className="flex flex-col gap-4 mb-4">
            <Typography variant="h6" className="font-normal text-gray-700">
              {t("workorder:report:sendReportByEmail", "Send report by email")}
            </Typography>

            <div className="p-4 border rounded-lg bg-white flex items-center justify-between gap-4">
              <div className="flex flex-col gap-1 flex-1">
                <Typography variant="h6" className="text-sm font-medium text-black">
                  {t("workorder:report:automaticEmail", "Automatic email")}
                </Typography>
                
                <Typography className="text-xs text-gray-600">
                  {t("workorder:report:reportWillBeSentTo", "The report will be sent to :")}
                </Typography>
                
                <ul className="list-disc list-inside my-1">
                  {isEmailMissing ? (
                    <li className="text-xs text-gray-900 font-medium list-none">
                      <span className="flex items-center gap-1 py-1">
                        ⛔ {t("workorder:report:noEmailSet", "no email set")} ⛔
                      </span>
                    </li>
                  ) : (
                    customerEmails.map((email, idx) => (
                      <li key={idx} className="text-xs text-gray-700">
                        {email}
                      </li>
                    ))
                  )}
                </ul>

                <Typography className="text-xs text-gray-500">
                  {t("workorder:report:deactivateOptionNote", "If you deactivate this option, the report will not be sent and you will have to download it and send on your own.")}
                </Typography>
              </div>

              <Switch 
                checked={!!sendReportEmail}
                onChange={setSendReportEmail || (() => {})}
              />
            </div>

            {sendReportEmail && customerLanguages.length > 0 && (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-2">
                  {customerLanguages.map((lang: any) => (
                    <div key={lang.id} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                      <div className="flex flex-col">
                        <Typography variant="h6" className="text-sm font-medium">
                          {lang.language.label}
                        </Typography>
                        <Typography className="text-xs text-gray-600">
                          {t("workorder:report:languageDescription", { language: lang.language.label })}
                        </Typography>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm"
                          variant="outlined"
                          color="blue"
                          className="flex items-center gap-1"
                          onClick={() => handleViewPdf && handleViewPdf(lang.language.isoCode)}
                        >
                          <EyeIcon className="w-4 h-4" />
                          {t("workorder:report:preview")}
                        </Button>
                        <Button 
                          size="sm"
                          color="blue"
                          className="flex items-center gap-1"
                          onClick={() => handleDownloadPdf && handleDownloadPdf(lang.language.isoCode)}
                        >
                          <DocumentChartBarIcon className="w-4 h-4" />
                          {t("workorder:report:download")}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sendReportEmail && (isEmailMissing || isLanguageMissing) && (
              <div className="flex flex-col gap-2">
                <Typography variant="h6" className="text-sm font-normal text-gray-600">
                  {t("workorder:report:customerSettings", "Customer's settings")}
                </Typography>
                <div className="p-4 border border-orange-200 bg-orange-50 rounded-lg">
                  {isEmailMissing && (
                    <div className="flex flex-col gap-1 mb-4">
                      <div className="flex items-center gap-2">
                        <ExclamationTriangleIcon className="h-5 w-5 text-gray-900" />
                        <Typography className="text-sm font-normal text-gray-900">
                          {t("workorder:report:emailMissing", "Email is missing")}
                        </Typography>
                      </div>
                      <div className="pl-7">
                        <Typography className="text-xs font-normal text-gray-900">
                          {t("workorder:report:emailMissingDesc", "This customer does not have any email set. Please set the email to send the report.")}
                        </Typography>
                      </div>
                    </div>
                  )}
                  {isLanguageMissing && (
                    <div className="flex flex-col gap-1 mb-4">
                      <div className="flex items-center gap-2">
                        <ExclamationTriangleIcon className="h-5 w-5 text-gray-900" />
                        <Typography className="text-sm font-normal text-gray-900">
                          {t("workorder:report:noLanguagesTitle")}
                        </Typography>
                      </div>
                      <div className="pl-7">
                        <Typography className="text-xs font-normal text-gray-900">
                          {t("workorder:report:noLanguagesDescription")}
                        </Typography>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between p-3 bg-white border border-gray-100 rounded-lg">
                    <CustomerDisplay customer={workorder.customer} size="small" className="text-gray-900 font-medium" />
                    {/* The detailed customer, not workorder.customer: the latter is a
                        CustomerSimpleResponse and would seed the form without reportTemplate or
                        sendInterventionReportEmail. */}
                    <CustomerEditButton
                      customer={customerDetail}
                      onEditStart={handleEditCustomer}
                      onCustomerUpdate={onCustomerUpdate || (async () => {})}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button
            onClick={onClose}
            variant="outlined"
            color="blue-gray"
            size="sm"
            className="normal-case border border-gray-300"
          >
            {t("common:cancel", "Cancel")}
          </Button>
          <Button 
            className={`${statusBgClass} normal-case shadow-none text-white hover:shadow-none`}
            onClick={onConfirm}
            size="sm"
            disabled={isConfirmDisabled}
          >
            {t("common:confirm", "Confirm")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}